import { useMemo, useState } from "react";
import { CalendarRange, ChevronDown, CopyPlus, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HorarioEditor, HORARIO_DEFECTO, HORARIO_FLEXIBLE_DEFECTO, horarioParaGuardar, normalizarHorario, resumenHorario } from "@/components/HorarioEditor";
import { MODO_FLEXIBLE, MODO_PATRON_TURNOS, patronTurnosParaGuardar } from "@/lib/horarioModos";

function genId() {
  return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function inicioAnio(y = new Date().getFullYear()) {
  return `${y}-01-01`;
}

function finAnio(y = new Date().getFullYear()) {
  return `${y}-12-31`;
}

function hoyIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function diaAnteriorIso(iso) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function esHastaAbierto(hasta) {
  if (!hasta) return true;
  const y = Number(String(hasta).slice(0, 4));
  return !Number.isFinite(y) || y >= 9000;
}

/** Periodo por defecto para un tramo nuevo: justo después del último «hasta» existente. */
function periodoTrasUltimoTramo(tramos) {
  let maxHasta = null;
  for (const t of tramos || []) {
    if (t?.hasta && (!maxHasta || t.hasta > maxHasta)) maxHasta = t.hasta;
  }
  if (!maxHasta) {
    const y = new Date().getFullYear();
    return { desde: inicioAnio(y), hasta: finAnio(y), abierto: false };
  }
  if (esHastaAbierto(maxHasta)) {
    return { desde: null, hasta: null, abierto: true };
  }
  const d = new Date(`${maxHasta}T12:00:00`);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return { desde: `${y}-${mm}-${dd}`, hasta: finAnio(y), abierto: false };
}

/**
 * Acorta/elimina tramos que solapan desde `desdeIso` e inserta un tramo nuevo
 * (útil para sustituir p. ej. el fijo del 01/10 por flexible).
 */
export function reemplazarTramosDesde(tramos, desdeIso, horarioSemanal = null, nombre = "Nuevo horario") {
  if (!desdeIso) return tramos || [];
  const finAnterior = diaAnteriorIso(desdeIso);
  const resultado = [];
  for (const t of tramos || []) {
    const td = t.desde || "0001-01-01";
    const th = t.hasta || "9999-12-31";
    if (th < desdeIso) {
      resultado.push(t);
      continue;
    }
    if (td < desdeIso) {
      resultado.push({ ...t, hasta: finAnterior });
    }
  }
  resultado.push({
    id: genId(),
    nombre,
    desde: desdeIso,
    hasta: "9999-12-31",
    horario: normalizarHorario(horarioSemanal || HORARIO_FLEXIBLE_DEFECTO),
  });
  return resultado;
}

export function aplicarVentanasFlexiblesATramos(tramos, { soloFuturos = true } = {}) {
  const hoy = hoyIso();
  return (tramos || []).map((t) => {
    if (soloFuturos && (t.hasta || "9999-12-31") < hoy) return t;
    return { ...t, horario: normalizarHorario(HORARIO_FLEXIBLE_DEFECTO) };
  });
}

/** Avanza una fecha ISO (YYYY-MM-DD) un año civil; 29-feb → 28-feb si el destino no es bisiesto. */
export function sumarAnioIso(iso) {
  if (!iso || iso.length < 10) return iso;
  const y = Number(iso.slice(0, 4)) + 1;
  const mmdd = iso.slice(4, 10);
  if (mmdd === "-02-29") {
    const bisiesto = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    if (!bisiesto) return `${y}-02-28`;
  }
  return `${y}${mmdd}`;
}

function fmtFechaCorta(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function esHorarioLegacy(h) {
  if (!h || typeof h !== "object") return false;
  if (Array.isArray(h.tramos)) return false;
  return ["lun", "mar", "mie"].some((k) => h[k] !== undefined);
}

export function tramoDefecto() {
  return {
    id: genId(),
    nombre: "Horario habitual",
    desde: inicioAnio(),
    hasta: finAnio(),
    horario: HORARIO_DEFECTO,
  };
}

export function normalizarTramo(t) {
  return {
    id: t.id || genId(),
    nombre: t.nombre || "Tramo",
    desde: t.desde || inicioAnio(),
    hasta: t.hasta || finAnio(),
    horario: normalizarHorario(t.horario),
  };
}

/** Convierte horario guardado (legacy o tramos) a lista editable de tramos. */
export function tramosFromHorario(h) {
  if (!h) return [tramoDefecto()];
  if (h.tramos?.length) return h.tramos.map(normalizarTramo);
  if (esHorarioLegacy(h)) {
    return [{ id: genId(), nombre: "Horario habitual", desde: inicioAnio(), hasta: finAnio(), horario: normalizarHorario(h) }];
  }
  return [tramoDefecto()];
}

export function erroresSolapeTramos(tramos) {
  if (!tramos?.length || tramos.length < 2) return [];

  const errores = [];
  for (let i = 0; i < tramos.length; i++) {
    const t = tramos[i];
    if (t.desde && t.hasta && t.desde > t.hasta) {
      errores.push(`El tramo «${t.nombre || `Tramo ${i + 1}`}»: la fecha de inicio es posterior a la de fin.`);
    }
  }
  for (let i = 0; i < tramos.length; i++) {
    for (let j = i + 1; j < tramos.length; j++) {
      const a = tramos[i];
      const b = tramos[j];
      const d1 = a.desde || "0001-01-01";
      const h1 = a.hasta || "9999-12-31";
      const d2 = b.desde || "0001-01-01";
      const h2 = b.hasta || "9999-12-31";
      if (d1 <= h2 && d2 <= h1 && d1 <= h1 && d2 <= h2) {
        const na = a.nombre || `Tramo ${i + 1}`;
        const nb = b.nombre || `Tramo ${j + 1}`;
        errores.push(
          `No es posible: el tramo «${na}» coincide en vigencia con el tramo «${nb}» ya definido.`,
        );
      }
    }
  }
  return errores;
}

export function horarioParaGuardarTramos(tramos, modoConfig = null) {
  if (!tramos?.length) return null;
  const base = {
    tramos: tramos.map((t) => ({
      id: t.id,
      nombre: t.nombre.trim() || "Tramo",
      desde: t.desde,
      hasta: t.hasta,
      horario: horarioParaGuardar(t.horario),
    })),
  };
  if (!modoConfig) return base;
  const out = { ...base, modo: modoConfig.modo || "fijo" };
  if (modoConfig.modo === MODO_FLEXIBLE) {
    out.flexible = { ...modoConfig.flexible };
  } else if (modoConfig.modo === MODO_PATRON_TURNOS) {
    out.patron_turnos = patronTurnosParaGuardar(modoConfig.patron);
  }
  return out;
}

export function resumenHorarioTramos(h) {
  if (!h) return "Sin horario configurado";
  // No inventar ene–dic: usar fechas reales del tramo (o indicar indefinido)
  if (h.tramos?.length) {
    if (h.tramos.length === 1) {
      const t = h.tramos[0];
      const fmt = (iso) =>
        iso
          ? new Date(iso + "T12:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" })
          : "—";
      return `${fmt(t.desde)} – ${fmt(t.hasta)} · ${resumenHorario(normalizarHorario(t.horario))}`;
    }
    return `${h.tramos.length} tramos · ${h.tramos.map((t) => t.nombre || "Tramo").join(", ")}`;
  }
  if (esHorarioLegacy(h)) {
    return resumenHorario(normalizarHorario(h));
  }
  return "Sin horario configurado";
}

export function HorarioTramosEditor({ tramos, onChange, prefix = "tramos", plantillas = [], modo = "fijo" }) {
  const lista = useMemo(() => (tramos?.length ? tramos : [tramoDefecto()]), [tramos]);
  /** Índices abiertos; por defecto todos plegados. */
  const [abiertos, setAbiertos] = useState(() => new Set());
  const [sustituirDesde, setSustituirDesde] = useState(() => {
    const futuro = (tramos || []).find((t) => (t.desde || "") >= hoyIso());
    return futuro?.desde || hoyIso();
  });

  const toggleTramo = (idx) => {
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const abrirTramo = (idx) => {
    setAbiertos((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const setTramo = (idx, patch) => {
    onChange(lista.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const plantillaNuevo = () =>
    modo === MODO_FLEXIBLE ? HORARIO_FLEXIBLE_DEFECTO : HORARIO_DEFECTO;

  const addTramo = () => {
    const periodo = periodoTrasUltimoTramo(lista);
    if (periodo.abierto) {
      // Último tramo abierto (p. ej. hasta 9999): no se puede «añadir después».
      // Redirigir a sustituir desde la fecha indicada.
      const desde = sustituirDesde || hoyIso();
      const next = reemplazarTramosDesde(
        lista,
        desde,
        plantillaNuevo(),
        modo === MODO_FLEXIBLE ? "Horario flexible" : "Nuevo horario"
      );
      onChange(next);
      abrirTramo(next.length - 1);
      return;
    }
    const nuevo = {
      id: genId(),
      nombre: `Tramo ${lista.length + 1}`,
      desde: periodo.desde,
      hasta: periodo.hasta,
      horario: normalizarHorario(plantillaNuevo()),
    };
    const nextIdx = lista.length;
    onChange([...lista, nuevo]);
    abrirTramo(nextIdx);
  };

  const sustituirDesdeFecha = () => {
    const desde = sustituirDesde || hoyIso();
    const next = reemplazarTramosDesde(
      lista,
      desde,
      plantillaNuevo(),
      modo === MODO_FLEXIBLE ? "Horario flexible" : "Nuevo horario"
    );
    onChange(next);
    abrirTramo(next.length - 1);
  };

  const duplicarTramoAnioSiguiente = (idx) => {
    const origen = lista[idx];
    if (!origen) return;
    const nuevo = {
      id: genId(),
      nombre: origen.nombre?.trim() || `Tramo ${lista.length + 1}`,
      desde: sumarAnioIso(origen.desde),
      hasta: sumarAnioIso(origen.hasta),
      horario: normalizarHorario(structuredClone(origen.horario)),
    };
    const nextIdx = lista.length;
    onChange([...lista, nuevo]);
    abrirTramo(nextIdx);
  };

  const removeTramo = (idx) => {
    if (lista.length <= 1) return;
    onChange(lista.filter((_, i) => i !== idx));
    setAbiertos((prev) => {
      const next = new Set();
      for (const i of prev) {
        if (i < idx) next.add(i);
        else if (i > idx) next.add(i - 1);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500">
        Define uno o varios tramos con fechas de vigencia. En cada periodo puedes configurar un horario semanal distinto.
        Las vigencias no pueden solaparse: al guardar se comprobará y no se permitirá si coinciden. Pulsa un tramo para editarlo.
      </p>
      {modo === MODO_FLEXIBLE && (
        <p className="text-xs text-amber-900 rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2">
          En flexible, las franjas son la <strong>ventana en la que se puede fichar</strong> (p. ej. 06:00–22:00).
          Las horas trabajadas se acumulan hasta el objetivo semanal/mensual; no hay horario fijo de entrada.
        </p>
      )}
      {lista.map((t, idx) => {
        const abierto = abiertos.has(idx);
        const anioDestino =
          Number((t.desde || t.hasta || "").slice(0, 4) || new Date().getFullYear()) + 1;
        return (
          <div
            key={t.id ? `${t.id}-${idx}` : `tramo-${idx}-${t.desde}-${t.hasta}`}
            className="rounded-xl border border-indigo-100 bg-indigo-50/30 overflow-hidden"
          >
            <div className="flex items-stretch gap-1">
              <button
                type="button"
                onClick={() => toggleTramo(idx)}
                className="flex min-w-0 flex-1 items-center gap-2 px-4 py-3 text-left hover:bg-indigo-50/80"
                aria-expanded={abierto}
                data-testid={`${prefix}-${idx}-toggle`}
              >
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-indigo-700 transition-transform ${abierto ? "" : "-rotate-90"}`}
                  strokeWidth={1.5}
                />
                <CalendarRange className="h-4 w-4 shrink-0 text-indigo-800" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-indigo-900">
                    {t.nombre?.trim() || `Tramo ${idx + 1}`}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    Vigencia: {fmtFechaCorta(t.desde)} – {fmtFechaCorta(t.hasta)}
                  </div>
                </div>
              </button>
              <div className="flex shrink-0 items-center gap-0.5 pr-2">
                <button
                  type="button"
                  onClick={() => duplicarTramoAnioSiguiente(idx)}
                  className="rounded-md p-1.5 text-zinc-400 hover:bg-indigo-100 hover:text-indigo-700"
                  title={`Duplicar tramo para ${anioDestino} (mismas fechas y horarios)`}
                  data-testid={`${prefix}-${idx}-duplicar`}
                >
                  <CopyPlus className="h-4 w-4" />
                </button>
                {lista.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTramo(idx)}
                    className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    title="Eliminar tramo"
                    data-testid={`${prefix}-${idx}-eliminar`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {abierto && (
              <div className="border-t border-indigo-100 px-4 pb-4 pt-3">
                <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5 sm:col-span-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input
                      value={t.nombre}
                      onChange={(e) => setTramo(idx, { nombre: e.target.value })}
                      placeholder="Ej. Horario verano"
                      data-testid={`${prefix}-${idx}-nombre`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Vigente desde</Label>
                    <Input
                      type="date"
                      value={t.desde}
                      onChange={(e) => setTramo(idx, { desde: e.target.value })}
                      data-testid={`${prefix}-${idx}-desde`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Vigente hasta</Label>
                    <Input
                      type="date"
                      value={t.hasta}
                      onChange={(e) => setTramo(idx, { hasta: e.target.value })}
                      data-testid={`${prefix}-${idx}-hasta`}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => duplicarTramoAnioSiguiente(idx)}
                    className="gap-1.5"
                    data-testid={`${prefix}-${idx}-duplicar-btn`}
                  >
                    <CopyPlus className="h-4 w-4" />
                    Duplicar tramo para {anioDestino}
                  </Button>
                </div>
                <HorarioEditor
                  horario={t.horario}
                  onChange={(h) => setTramo(idx, { horario: h })}
                  prefix={`${prefix}-${idx}`}
                  plantillas={plantillas}
                  maxTurnosPorDia={modo === MODO_FLEXIBLE ? 1 : null}
                />
              </div>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-indigo-200 bg-white/60 p-3">
        <div className="space-y-1">
          <Label className="text-xs">Sustituir horario desde</Label>
          <Input
            type="date"
            value={sustituirDesde}
            onChange={(e) => setSustituirDesde(e.target.value)}
            data-testid={`${prefix}-sustituir-desde`}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={sustituirDesdeFecha}
          className="gap-1.5"
          data-testid={`${prefix}-sustituir-btn`}
        >
          Sustituir desde esa fecha
        </Button>
        <p className="basis-full text-[11px] text-zinc-500">
          Acorta o elimina los tramos que solapan y crea uno nuevo (p. ej. sustituir el fijo del 01/10 por flexible).
        </p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addTramo} className="gap-1.5" data-testid={`${prefix}-add-tramo`}>
        <Plus className="h-4 w-4" />
        Añadir tramo de fechas
      </Button>
    </div>
  );
}
